/**
 * Anotar una cita a mano, dentro de un servicio.
 *
 * Existe porque el teléfono sigue sonando: la mitad de las citas de una
 * estética se piden hablando, y sin esto habría que decirle a quien acaba de
 * pedirla de viva voz que entre a la web a reservarla.
 *
 * Las horas no se escriben: se eligen de los huecos que calcula el servidor,
 * que son exactamente los que ve quien reserva desde la página pública. Dejar
 * teclear la hora convertía esto en la forma de meter citas fuera del propio
 * horario sin enterarse.
 *
 * Vive dentro de la ficha de un servicio, así que no pregunta cuál: ya se sabe.
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { IconCalendarEvent, IconCalendarOff, IconCalendarPlus, IconCheck, IconChevronDown, IconLoader2, IconX } from '@tabler/icons-react'
import {
  createBooking,
  listBookingSlots,
  type Booking,
  type BookingSlot,
  type BookingResource,
  type Offer,
} from '../../../lib/api'
import { bookingError, timeIn } from './bookingShared'
import { formatMoney, mainPrice } from './offerShared'
import { activeLanguage, useI18n } from '../../../lib/i18n'
import { SingleDatePicker } from '../DateRangePicker'
import { usePhoneViewport } from '../../../hooks/usePhoneViewport'
import { SlotsEmpty } from './SlotsEmpty'
import { useScrollTopOnStep } from '../../../lib/useScrollTopOnStep'

function todayIn(timeZone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(new Date())
    const value = (part: Intl.DateTimeFormatPartTypes) => parts.find(({ type }) => type === part)?.value
    const year = value('year')
    const month = value('month')
    const day = value('day')
    return year && month && day ? `${year}-${month}-${day}` : new Date().toISOString().slice(0, 10)
  } catch {
    return new Date().toISOString().slice(0, 10)
  }
}

function dayLabel(day: string): string {
  /* En el idioma de quien mira el panel. Estaba clavado en `es-MX`, así que el
     pie del diálogo decía «mié 20 may» dentro de un panel en inglés. */
  return new Intl.DateTimeFormat(activeLanguage(), {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  }).format(new Date(`${day}T12:00:00Z`))
}

export function NewBookingDialog({ projectId, token, service, services, timezone, initialDay, resources = [], onClose, onCreated }: {
  projectId: string
  token: string
  /**
   * Con cuál se abre, si es que se sabe.
   *
   * Ausente cuando no se sabe —el «+» de un día con el catálogo sin filtrar— y
   * entonces elegirlo es el primer paso: de él salen la duración, el horario y
   * los huecos, así que rellenar lo demás antes es rellenar contra nada.
   */
  service?: Offer | undefined
  /**
   * Los servicios con agenda entre los que elegir.
   *
   * Antes el diálogo recibía UNO y punto, así que el «+» de un día sólo podía
   * salir con el catálogo filtrado: sin filtro no había forma de saber contra
   * qué servicio se estaba apuntando. Eligiéndolo aquí dentro, el «+» vale
   * siempre — que es lo que se espera al pasar el ratón por un día.
   */
  services?: Offer[] | undefined
  timezone: string
  initialDay?: string | undefined
  /** El equipo, para elegir con quién cuando el servicio lo dan varios. */
  resources?: BookingResource[] | undefined
  onClose: () => void
  onCreated: (booking: Booking) => void
}) {
  const { t } = useI18n()
  /* El servicio elegido manda sobre el que llegó: de él salen la duración, el
     horario y los huecos, así que cambiarlo vuelve a pedirlos. */
  const [chosen, setChosen] = useState<Offer | null>(service ?? null)
  /*
   * Con quién y cuántos. La misma regla que el worker (`offerResources`): la
   * lista del servicio sin los inactivos, o su recurso único, o el primero.
   */
  const team = (() => {
    const active = resources.filter((row) => row.active)
    const listed = active.filter((row) => chosen?.booking.resourceIds?.includes(row.id))
    if (listed.length) return listed
    const single = resources.find((row) => row.id === chosen?.booking.resourceId) ?? resources[0]
    return single ? [single] : []
  })()
  const [resourceId, setResourceId] = useState('')
  const [party, setParty] = useState(1)
  const who = team.find((row) => row.id === resourceId) ?? team[0]
  const whoId = who?.id
  const maxParty = chosen && chosen.booking.capacity > 1 ? chosen.booking.capacity : 1
  /* Por días: no hay horas; se elige el día de inicio y, si el servicio deja,
     cuántos días. */
  const byDays = chosen?.booking.unit === 'day'
  const daysMin = Math.max(1, chosen?.booking.daysMin ?? 1)
  const daysMax = Math.max(daysMin, chosen?.booking.daysMax ?? daysMin)
  const [daysWanted, setDays] = useState(1)
  const days = Math.min(daysMax, Math.max(daysMin, daysWanted))
  /** «Todo el día» o «20 oct → 22 oct»: lo que se lee en vez de «00:00». */
  const slotLabel = (startsAt: string, endsAt?: string) => {
    if (!byDays) return timeIn(startsAt, slotTimezone)
    if (days <= 1 || !endsAt) return t('bookings.allDay')
    const format = new Intl.DateTimeFormat(activeLanguage(), { timeZone: slotTimezone, day: 'numeric', month: 'short' })
    return `${format.format(new Date(startsAt))} → ${format.format(new Date(Date.parse(endsAt) - 60_000))}`
  }
  const options = services && services.length > 0 ? services : service ? [service] : []
  const price = chosen ? mainPrice(chosen) : null
  const [day, setDay] = useState(() => initialDay ?? todayIn(timezone))
  const [slot, setSlot] = useState('')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [note, setNote] = useState('')
  const [servicePickerOpen, setServicePickerOpen] = useState(false)
  const servicePickerTriggerRef = useRef<HTMLButtonElement>(null)
  const servicePickerPopoverRef = useRef<HTMLDivElement>(null)
  const [servicePickerPosition, setServicePickerPosition] = useState<{ top: number; left: number; width: number; above: boolean } | null>(null)

  const [slots, setSlots] = useState<BookingSlot[]>([])
  const [slotTimezone, setSlotTimezone] = useState(timezone)
  const [slotsLoading, setSlotsLoading] = useState(false)
  const [slotsError, setSlotsError] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  /* En el teléfono, un asistente de pasos —servicio, fecha y hora, datos— en vez
     de las tres columnas apiladas en una sola pantalla larguísima. Sin paso de
     servicio cuando ya se sabe cuál es y no hay otro entre el que elegir. */
  const isPhone = usePhoneViewport()
  const steps: Array<'service' | 'when' | 'who'> = options.length > 1 || !service ? ['service', 'when', 'who'] : ['when', 'who']
  const [stepIndex, setStepIndex] = useState(0)
  const currentStep = steps[Math.min(stepIndex, steps.length - 1)]!
  const isLastStep = stepIndex >= steps.length - 1
  const contentRef = useRef<HTMLDivElement>(null)
  useScrollTopOnStep(stepIndex, contentRef)


  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (servicePickerOpen) { setServicePickerOpen(false); return }
      if (!saving) onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose, saving, servicePickerOpen])

  /* No es un `select` nativo: Safari conserva su flecha y su relleno aunque se
     le dé estilo. El menú propio deja el mismo campo en todos los navegadores y
     se porta al `body` para que el diálogo no lo recorte. */
  useEffect(() => {
    if (!servicePickerOpen) {
      setServicePickerPosition(null)
      return
    }
    const updatePosition = () => {
      const rect = servicePickerTriggerRef.current?.getBoundingClientRect()
      if (!rect) return
      const margin = 12
      const menuHeight = Math.min(280, 48 * Math.max(options.length, 1) + 16)
      const above = window.innerHeight - rect.bottom < menuHeight && rect.top > menuHeight
      setServicePickerPosition({
        top: above ? rect.top - 8 : rect.bottom + 8,
        left: Math.max(margin, Math.min(rect.left, window.innerWidth - rect.width - margin)),
        width: Math.min(rect.width, window.innerWidth - margin * 2),
        above,
      })
    }
    const closeFromOutside = (event: PointerEvent) => {
      const target = event.target as Node
      if (!servicePickerTriggerRef.current?.contains(target) && !servicePickerPopoverRef.current?.contains(target)) setServicePickerOpen(false)
    }
    updatePosition()
    document.addEventListener('pointerdown', closeFromOutside)
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      document.removeEventListener('pointerdown', closeFromOutside)
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [servicePickerOpen, options.length])

  // Se limpia la elección al cambiar de día: un hueco de ayer seguiría marcado
  // y se enviaría sin que nadie volviera a mirarlo.
  useEffect(() => {
    let cancelled = false
    setSlot('')
    /* Sin servicio no hay a qué preguntarle por huecos: se deja la lista vacía y
       la columna explica que falta elegirlo. */
    if (!chosen) { setSlots([]); setSlotsLoading(false); return }
    setSlotsLoading(true)
    listBookingSlots(projectId, { offerId: chosen.id, from: day, to: day, ...(whoId ? { resourceId: whoId } : {}), party: Math.min(party, maxParty), ...(byDays ? { days } : {}) }, token)
      .then((result) => {
        if (cancelled) return
        setSlots(result.slots)
        setSlotTimezone(result.timezone)
        setSlotsError('')
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setSlots([])
        setSlotsError(bookingError(err))
      })
      .finally(() => { if (!cancelled) setSlotsLoading(false) })
    return () => { cancelled = true }
  }, [projectId, token, chosen, day, whoId, party, maxParty, byDays, days])

  /* Lo que hace falta para que esto sea una cita: contra qué, cuándo, y de
     quién. El correo y el teléfono no —hay quien reserva por teléfono y no deja
     ninguno de los dos— pero un nombre siempre hay, y sin él la cita aparece en
     el calendario sin poder decir de quién es. */
  const ready = Boolean(chosen) && Boolean(slot) && name.trim().length > 0
  /* Los datos de cliente van al final: antes de fijar un hueco no hay cita a la
     que asociarlos. Dejar escribirlos antes hacía natural rellenar la columna
     derecha y descubrir tarde que aún faltaban fecha y hora. */
  const clientDetailsReady = Boolean(chosen && slot)
  const canAdvance = currentStep === 'service' ? Boolean(chosen) : currentStep === 'when' ? Boolean(chosen && slot) : ready

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!chosen) { setError(t('bookings.new.needService')); return }
    if (!slot) { setError(t('bookings.new.needSlot')); return }
    if (!name.trim()) { setError(t('bookings.new.needName')); return }
    setError('')
    setSaving(true)
    try {
      onCreated(await createBooking(
        projectId,
        {
          offerId: chosen.id,
          ...(who ? { resourceId: who.id } : {}),
          partySize: Math.min(party, maxParty),
          ...(byDays ? { days } : {}),
          startsAt: slot,
          title: chosen.title,
          customerName: name,
          customerEmail: email,
          customerPhone: phone,
          customerNote: note,
        },
        token,
      ))
    } catch (err) {
      setError(bookingError(err))
    } finally {
      setSaving(false)
    }
  }

  return createPortal(
    <div
      className="dash-modal-backdrop dash-new-booking-backdrop"
      onPointerDown={(event) => { if (event.target === event.currentTarget && !saving) onClose() }}
    >
      <section className="dash-new-booking-dialog" role="dialog" aria-modal="true" aria-labelledby="new-booking-title">
        {/* Una ventana emergente y no el asistente a pantalla completa: dar de
            alta una cita es un momento sobre el calendario, que tiene que seguir
            viéndose detrás. Sin icono en caja: el título dice qué es esto y la
            frase de debajo, para qué servicio. */}
        <header className="dash-new-booking-head">
          <div className="dash-new-booking-head-copy">
            <h2 id="new-booking-title">{t('bookings.new.title')}</h2>
            {/* Partida en dos porque el nombre del servicio va en negrita en
                medio: una sola clave con marcado dentro no la traduce nadie. */}
            {chosen ? <p>{t('bookings.new.lead1')} <strong>{chosen.title}</strong>{t('bookings.new.lead2')}</p> : null}
            {/* Sólo en el teléfono: en qué paso va y una barra de lo que falta. */}
            <span className="dash-new-booking-steps">{t('bookings.new.stepOf', { current: Math.min(stepIndex, steps.length - 1) + 1, total: steps.length })}</span>
          </div>
          <button type="button" className="dash-create-close" aria-label={t('bookings.new.close')} disabled={saving} onClick={onClose}>
            <IconX size={16} />
          </button>
        </header>

        <div className="dash-new-booking-progress" aria-hidden="true">
          <span style={{ width: `${((Math.min(stepIndex, steps.length - 1) + 1) / steps.length) * 100}%` }} />
        </div>

        <form className="dash-new-booking-form" onSubmit={(event) => { void submit(event) }}>
          <div className="dash-new-booking-content" ref={contentRef}>
            {/*
              Qué se reserva, a la izquierda y con su cara.
              
              Es la mitad izquierda de la página pública traída aquí: portada,
              nombre y los datos duros —cuánto dura, cuánto cuesta, cuánta gente
              cabe—. Quien apunta una cita por teléfono está leyendo en voz alta
              exactamente eso, y hasta ahora lo único que veía del servicio era
              su nombre en un desplegable.
            */}
            <aside className={`dash-new-booking-service${chosen ? '' : ' is-unselected'}${currentStep === 'service' ? '' : ' is-off-step'}`}>
              {/* La portada sólo representa un servicio que ya se eligió. Antes,
                  un calendario gris ocupaba esta columna en vacío y se leía
                  como una segunda elección o una acción deshabilitada. */}
              {chosen ? (
                chosen.images[0] ? (
                  <img className="dash-new-booking-shot" src={chosen.images[0]} alt="" />
                ) : (
                  <div className="dash-new-booking-shot is-empty" aria-hidden="true"><IconCalendarEvent size={26} /></div>
                )
              ) : null}
              <h3>{chosen ? chosen.title : t('bookings.new.noServiceTitle')}</h3>
              {/* Los datos duros en UNA línea separada por puntos, no en tres
                  pastillas de colores: son un pie de foto, no tres decisiones.
                  El precio en el mismo gris — en el acento se leía como el botón
                  de la tarjeta. */}
              <p className="dash-new-booking-facts">
                {chosen
                  ? [
                    chosen.booking.durationMinutes ? t('bookings.new.minutes', { count: chosen.booking.durationMinutes }) : '',
                    chosen.booking.capacity > 1 ? t('bookings.new.capacity', { count: chosen.booking.capacity }) : '',
                    price ? formatMoney(price.unitAmount, price.currency) : '',
                  ].filter(Boolean).join(' · ')
                  : t('bookings.new.noServiceBody')}
              </p>
              {/* El cambio de servicio, aquí: es la ficha que se está leyendo, no
                  un campo del formulario. Con uno solo no se pinta — un
                  desplegable de una opción es un mando que sólo se puede dejar
                  como está. */}
              {/* Siempre visible mientras no haya uno elegido, aunque sólo haya
                  un servicio: es el primer paso, y esconderlo dejaría la
                  pantalla pidiendo un día sin decir de qué. */}
              {options.length > 1 || !chosen ? (
                <label className="dash-new-booking-field">
                  <span>{t('bookings.new.service')}</span>
                  <button
                    ref={servicePickerTriggerRef}
                    type="button"
                    className={`dash-new-booking-service-select${chosen ? '' : ' is-placeholder'}`}
                    aria-haspopup="listbox"
                    aria-expanded={servicePickerOpen}
                    onClick={() => setServicePickerOpen((open) => !open)}
                  >
                    <span>{chosen?.title ?? t('bookings.new.servicePlaceholder')}</span>
                    <IconChevronDown size={18} aria-hidden="true" />
                  </button>
                  {servicePickerOpen && servicePickerPosition ? createPortal(
                    <div
                      ref={servicePickerPopoverRef}
                      className={`dash-new-booking-service-options${servicePickerPosition.above ? ' is-above' : ''}`}
                      role="listbox"
                      aria-label={t('bookings.new.service')}
                      style={{ top: servicePickerPosition.top, left: servicePickerPosition.left, width: servicePickerPosition.width }}
                    >
                      {options.map((row) => (
                        <button
                          key={row.id}
                          type="button"
                          role="option"
                          aria-selected={row.id === chosen?.id}
                          className={row.id === chosen?.id ? 'is-selected' : ''}
                          onClick={() => {
                            setChosen(row)
                            setServicePickerOpen(false)
                          }}
                        >
                          <span>{row.title}</span>
                          {row.id === chosen?.id ? <IconCheck size={16} aria-hidden="true" /> : null}
                        </button>
                      ))}
                    </div>,
                    document.body,
                  ) : null}
                </label>
              ) : null}
              {chosen && team.length > 1 ? (
                <label className="dash-new-booking-field">
                  <span>{t('bookings.detail.withWhom')}</span>
                  <select value={who?.id ?? ''} onChange={(event) => setResourceId(event.target.value)}>
                    {team.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
                  </select>
                </label>
              ) : null}
              {chosen && maxParty > 1 ? (
                <label className="dash-new-booking-field">
                  <span>{t('bookings.detail.people')}</span>
                  <input
                    type="number"
                    min={1}
                    max={maxParty}
                    value={Math.min(party, maxParty)}
                    onChange={(event) => setParty(Math.max(1, Math.min(maxParty, Number(event.target.value) || 1)))}
                  />
                </label>
              ) : null}
              {chosen && byDays && daysMax > daysMin ? (
                <label className="dash-new-booking-field">
                  <span>{t('bookings.new.days')}</span>
                  <input
                    type="number"
                    min={daysMin}
                    max={daysMax}
                    value={days}
                    onChange={(event) => setDays(Number(event.target.value) || daysMin)}
                  />
                </label>
              ) : null}
            </aside>

            <section className={`dash-new-booking-section${currentStep === 'when' ? '' : ' is-off-step'}`}>
              <header><strong>{t('bookings.new.whenTitle')}</strong></header>

              <div className="dash-new-booking-section-body">
                <label className="dash-new-booking-field">
                  <span>{t('bookings.new.day')}</span>
                  <SingleDatePicker
                    value={day}
                    disabled={!chosen}
                    timezone={timezone}
                    onChange={setDay}
                  />
                </label>

                <div className="dash-new-booking-field">
                  <span>{t('bookings.new.slot')} <small>{slotTimezone}</small></span>
                  {/* Sin servicio elegido no se pinta nada: el propio paso —o la
                      columna— ya pide elegirlo, y un aviso vacío aquí sólo repetía
                      lo mismo. */}
                  {!chosen ? null : slotsLoading ? (
                    <p className="dash-new-booking-empty"><IconLoader2 className="dash-spin" size={15} /> {t('bookings.new.searching')}</p>
                  ) : slotsError ? (
                    <p className="dash-contact-add-error">{slotsError}</p>
                  ) : slots.length === 0 ? (
                    <SlotsEmpty icon={<IconCalendarOff size={20} />} title={t('bookings.new.noSlotsTitle')} body={t('bookings.new.noSlotsBody')} />
                  ) : (
                    <div className="dash-new-booking-slots" aria-label={t('bookings.new.slotsAria')}>
                      {slots.map((option) => (
                        <button
                          key={option.startsAt}
                          type="button"
                          className={option.startsAt === slot ? 'is-selected' : ''}
                          aria-pressed={option.startsAt === slot}
                          onClick={() => setSlot(option.startsAt)}
                        >
                          <strong>{slotLabel(option.startsAt, option.endsAt)}</strong>
                          {option.seatsLeft > 1
                            ? <small>{t('bookings.new.seatsLeft', { count: option.seatsLeft })}</small>
                            : <small>{t('bookings.new.available')}</small>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </section>

            <section className={`dash-new-booking-section${currentStep === 'who' ? '' : ' is-off-step'}`}>
              <header><strong>{t('bookings.new.whoTitle')}</strong></header>

              <div className="dash-new-booking-section-body dash-new-booking-client-fields">
                <label className="dash-new-booking-field is-wide">
                  {/* El único obligatorio de los tres: sin él la cita sale en el
                      calendario sin poder decir de quién es. Correo y teléfono
                      no —hay quien reserva por teléfono y no deja ninguno—. */}
                  <span>{t('bookings.new.name')} <small>{t('bookings.new.required')}</small></span>
                  <input value={name} maxLength={120} required disabled={!clientDetailsReady} placeholder={t('bookings.new.namePlaceholder')} onChange={(event) => setName(event.target.value)} />
                </label>

                <label className="dash-new-booking-field">
                  <span>{t('bookings.new.email')}</span>
                  <input type="email" value={email} maxLength={254} disabled={!clientDetailsReady} placeholder={t('bookings.new.emailPlaceholder')} onChange={(event) => setEmail(event.target.value)} />
                </label>

                <label className="dash-new-booking-field">
                  <span>{t('bookings.new.phone')}</span>
                  <input value={phone} maxLength={40} disabled={!clientDetailsReady} placeholder={t('bookings.new.phonePlaceholder')} onChange={(event) => setPhone(event.target.value)} />
                </label>

                <label className="dash-new-booking-field is-wide">
                  <span>{t('bookings.new.note')} <small>{t('bookings.new.optional')}</small></span>
                  <textarea rows={3} maxLength={2000} disabled={!clientDetailsReady} placeholder={t('bookings.new.notePlaceholder')} value={note} onChange={(event) => setNote(event.target.value)} />
                </label>
              </div>
            </section>
          </div>

          {error ? <p className="dash-new-booking-error" role="alert">{error}</p> : null}

          <footer className="dash-new-booking-foot">
            {/* Qué falta, en el orden en que se rellena: primero el servicio,
                luego la hora, luego el nombre. Con todo puesto, el resumen de lo
                que se va a crear. */}
            <span className={ready ? 'has-slot' : ''}>
              {!chosen
                ? t('bookings.new.pickService')
                : !slot
                  ? t('bookings.new.pickSlot')
                  : !name.trim()
                    ? t('bookings.new.needName')
                    : `${dayLabel(day)} · ${slotLabel(slot, slots.find((option) => option.startsAt === slot)?.endsAt)}`}
            </span>
            {isPhone ? (
              /* El asistente: «Atrás» —o «Cancelar» en el primer paso— y «Siguiente»,
                 que en el último es «Reservar». */
              <div>
                <button
                  type="button"
                  className="dash-secondary-btn"
                  disabled={saving}
                  onClick={() => { if (stepIndex > 0) setStepIndex(stepIndex - 1); else onClose() }}
                >
                  {stepIndex > 0 ? t('bookings.new.back') : t('bookings.new.cancel')}
                </button>
                {isLastStep ? (
                  <button type="submit" className="dash-primary-btn" disabled={saving || !ready}>
                    {saving ? <IconLoader2 className="dash-spin" size={14} /> : <IconCalendarPlus size={14} />}
                    {t('bookings.new.book')}
                  </button>
                ) : (
                  <button type="button" className="dash-primary-btn" disabled={!canAdvance} onClick={() => setStepIndex(stepIndex + 1)}>
                    {t('bookings.new.next')}
                  </button>
                )}
              </div>
            ) : (
              <div>
              <button type="button" className="dash-secondary-btn" disabled={saving} onClick={onClose}>{t('bookings.new.cancel')}</button>
              <button type="submit" className="dash-primary-btn" disabled={saving || !ready}>
                {saving ? <IconLoader2 className="dash-spin" size={14} /> : <IconCalendarPlus size={14} />}
                {t('bookings.new.create')}
              </button>
              </div>
            )}
          </footer>
        </form>
      </section>
    </div>,
    document.body,
  )
}
